import * as React from 'react';
import * as Yup from 'yup';
import CreatableSelect from 'react-select/creatable';
import Select, { SingleValue } from 'react-select';
import clsx from 'clsx';
import { Col, Row } from 'react-bootstrap';
import { FC, useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import { citylisting } from 'src/_metronic/layout/components/sidebar/sidebar-menu/SidebarMenuMain';
import { PartysListLoading } from '../components/loading/CustomersListLoading';
import { createParty, updateParty, uniqueCode, getEditParty } from '../core/_requests';
import { createCity } from '../core/_requests';
import { setMessage } from '../../../../../store/alertMessage/actions';
import { useListView } from '../core/ListViewProvider';
import { useQueryResponse } from '../core/QueryResponseProvider';
import { useParams } from 'react-router-dom';
import { useNavigate, useLocation } from 'react-router-dom';

type Props = {
  isPartyLoading: boolean;
  party: any;
};

type LocationState = {
  partyId?: string;
};

const PartyEditModalForm: FC<Props> = ({ party, isPartyLoading }) => {
  const intl = useIntl();
  const { itemIdForUpdate, setItemIdForUpdate } = useListView();
  const { refetch } = useQueryResponse();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [codeError, setCodeError] = useState<boolean>(false);
  const [cityOptions, setCityOptions] = useState<{ label: string; value: string }[]>([]);
  const [data, setData] = useState<any>([]);
  const partyNameInputRef = useRef<HTMLInputElement>(null);
  const pnameInputRef = useRef<HTMLInputElement>(null);
 
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const id = state?.partyId;
    document.title = `PANEL | Add Party`

  // Reference for the "Vatav" input field
  const vatavInputRef = useRef<HTMLInputElement>(null);
 const phoneInputRef = useRef<HTMLInputElement>(null);
 const addressInputRef = useRef<HTMLInputElement>(null);
const receiverLimitRef = useRef<HTMLInputElement>(null);
const senderLimitRef = useRef<HTMLInputElement>(null);
const openingBalanceRef = useRef<HTMLInputElement>(null);
const crdrRef = useRef<any>(null);
const btnRef = useRef<any>(null);

  const getEditData = async (id: string) => {
    try {
      const response = await getEditParty(id);
      setData(response);
    } catch (error) {
      console.error('Error checking parties code uniqueness', error);
      return false;
    }
  };

  useEffect(() => {
    if (id) {
      getEditData(id);
    }
  }, [id]);
  useEffect(() => {
    if (id && partyNameInputRef.current) {
      setTimeout(() => {
        partyNameInputRef.current?.focus();
      }, 100); // Slight delay ensures value is set before focus
    }
  }, [id]);
  const addCity = async (cityName: string) => {
    try {
      const branchData = localStorage.getItem('branch');
      if (!branchData) {
        throw new Error('Branch data not found in localStorage');
      }
      const branch = JSON.parse(branchData);
      const branchId = branch.id;

      if (!branchId) {
        throw new Error('Branch ID not found in localStorage data');
      }
      const requestBody = {
        city_name: cityName,
        branch_id: parseInt(branchId, 10),
      };
      const response = await createCity(requestBody);
      dispatch(setMessage('success', response?.message));
      return response.data;
    } catch (error) {
      console.error('Error adding city:', error);
      return null;
    }
  };

  const optionOfCrDr: any = [
    { value: 'CR', label: `${intl.formatMessage({ id: 'CREDIT' })}` },
    { value: 'DR', label: `${intl.formatMessage({ id: 'DEBIT' })}` },
  ];

  const cancel = (withRefresh?: boolean) => {
    navigate("/partys/list-party")
    if (withRefresh) {
      refetch();
    }
    setItemIdForUpdate(undefined);
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const checkPartiesCodeUnique = async (code: string) => {
    try {
      const response = await uniqueCode(code);
      if (response?.responseData?.isUnique === true) {
        setCodeError(true);
      } else {
        setCodeError(false);
      }
      return response?.responseData?.isUnique;
    } catch (error) {
      console.error('Error checking parties code uniqueness', error);
      return false;
    }
  };

  const PartyManagementData = {
    party_code: '',
    party_name: '',
    party_phone: '',
    party_address: '',
    city_id: '',
    party_vatav: '',
    party_lena_limit: '',
    party_dena_limit: '',
    party_opening_balance: '',
    opening_bal_crordr: 'CR',
  };

  const editPartySchema = Yup.object().shape({
    party_code: Yup.string().required(`${intl.formatMessage({ id: 'PARTY.CODE.IS.REQUIRED' })}`),
    party_name: Yup.string()
      .min(3, `${intl.formatMessage({ id: 'PARTY.NAME.MINIMUM.LENGTH' })}`)
      .max(50, `${intl.formatMessage({ id: 'PARTY.NAME.MAXIMUM.LENGTH' })}`)
      .required(`${intl.formatMessage({ id: 'PARTY.NAME.IS.REQUIRED' })}`),
    city_id: Yup.string().required(`${intl.formatMessage({ id: 'PARTY.CITY.IS.REQUIRED' })}`),
    party_lena_limit: Yup.string().required(
      `${intl.formatMessage({ id: 'PARTY.RECEIVERLIMIT.IS.REQUIRED' })}`
    ),
    party_dena_limit: Yup.string().required(
      `${intl.formatMessage({ id: 'PARTY.SENDERLIMIT.IS.REQUIRED' })}`
    ),
    party_opening_balance: Yup.string().required(
      `${intl.formatMessage({ id: 'PARTY.OPENING.BALANCE.REQUIRED' })}`
    ),
  });

  const formik = useFormik({
    initialValues: PartyManagementData,
    validationSchema: editPartySchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      let sendData = JSON.parse(JSON.stringify(values));
      setSubmitting(true);
      setLoading(true);

      const modifiedValues = {
        ...sendData,
        party_name: sendData.party_name,
        party_vatav: sendData.party_vatav.toString(),
        city_id: sendData.city_id.toString(),
      };

      try {
        let response;
        if (id) {
          response = await updateParty(modifiedValues, id);
          if (response.message) {
           
            setSubmitting(false);
            resetForm();
            refetch();
            dispatch(setMessage('success', response?.message || 'Party Updated Successfully!'));
            setItemIdForUpdate(undefined);
            navigate('/partys/list-party');
          } else {
            dispatch(
              setMessage(
                'error',
                response?.responseData?.msg ?? response?.responseMessage ?? 'Something went wrong'
              )
            );
          }
          setLoading(false);
        } else {
          response = await createParty(modifiedValues);
          if (response.status === 201 || response.status === 200) {
        
            setSubmitting(false);
            resetForm();
            refetch();
            dispatch(setMessage('success', response?.data?.message || 'Party Added Successfully!'));
            setTimeout(() => {
              navigate('/partys/list-party');
            }, 3000);
            setItemIdForUpdate(undefined);
          } else {
            dispatch(
              setMessage(
                'error',
                response?.responseData?.msg ?? response?.responseMessage ?? 'Something went wrong'
              )
            );
          }
          setLoading(false);
        }
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.Error?.message ||
          error?.message ||
          'Error occurred';
        dispatch(setMessage('error', errorMessage));
        resetForm();
        formik.setFieldValue('city_id', '');
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (id && data) {
      formik.setFieldValue('party_code', data?.data?.party_code);
      formik.setFieldValue('party_name', data?.data?.party_name);
      formik.setFieldValue('party_phone', data?.data?.party_phone);
      formik.setFieldValue('party_address', data?.data?.party_address);
      formik.setFieldValue('city_id', data?.data?.city_id);
      formik.setFieldValue('party_vatav', data?.data?.party_vatav);
      formik.setFieldValue('party_lena_limit', data?.data?.party_lena_limit);
      formik.setFieldValue('party_dena_limit', data?.data?.party_dena_limit);
      const openingBalance = data?.data?.party_opening_balance;
      const positiveOpeningBalance = Math.abs(openingBalance);
      formik.setFieldValue('party_opening_balance', positiveOpeningBalance);
      if (openingBalance < 0) {
        formik.setFieldValue('opening_bal_crordr', 'DR');
      } else {
        formik.setFieldValue('opening_bal_crordr', 'CR');
      }
    }
  }, [id, data]);

  useEffect(() => {
    if (!cityOptions.length && formik.values.city_id) {
      citylisting().then((cities) => setCityOptions(cities));
    }
  }, [formik.values.city_id, cityOptions]);

  const customSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      borderColor: '#E4E6EF',
      borderWidth: '2px',
      minHeight: 'calc(1.5em + .75rem + 16px)',
      borderRadius: '0.475rem',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#cdcdde',
      },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      lineHeight: '1.5',
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: 'calc(1.5em + .75rem + 16px)',
    }),
  };

  const citySelectRef = useRef<any>(null);

  const handleCityFocus = () => {
    if (citySelectRef.current) {
      citySelectRef.current.openMenu();
    }
  };

  // Add keyboard handling for city selection
  const handleCityKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      if (!formik.values.city_id) {
        e.preventDefault(); // Prevent default Tab behavior
        formik.setFieldValue('city_id', ''); // Ensure no city is selected
        if (vatavInputRef.current) {
          vatavInputRef.current.focus(); // Move focus to Vatav field
        }
      }
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.target;
    // Set cursor at the end
    const length = input.value.length;
    input.setSelectionRange(length, length);
  };
  return (
    <>
      <form
        id="kt_modal_add_Party_form"
        className="form"
        onSubmit={formik.handleSubmit}
        // onKeyDown={(e) => {
        //   if (e.key === 'Enter' && !(e.target instanceof HTMLInputElement && e.target.closest('.react-select__input'))) {
        //     e.preventDefault();
        //     formik.handleSubmit();
        //   }
        // }}
      >
        <div
          className="d-flex flex-column mb-5 me-n7 pe-4 ps-2"
          id="kt_modal_add_Party_scroll"
          data-kt-scroll="true"
          data-kt-scroll-activate="{default: false, lg: true}"
          data-kt-scroll-max-height="auto"
          data-kt-scroll-dependencies="#kt_modal_add_Party_header"
          data-kt-scroll-wrappers="#kt_modal_add_Party_scroll"
          data-kt-scroll-offset="300px"
        >
          <Row className="mb-8">
            <Col md={4}>
              <label className="required fw-bold fs-6 mb-2">Party Code</label>
              {id ? (
                <input
                  type="text"
                  name="party_code"
                  className="form-control"
                  autoComplete="off"
                  placeholder="Party Code"
                  value={formik.values.party_code}
                  disabled
                />
              ) : (
                <input
                  type="text"
                  name="party_code"
                  className="form-control"
                  autoComplete="off"
                  ref={inputRef}
                  maxLength={40}
                  placeholder="Party Code"
                  onKeyDown={(e: any) => {
                    if (!/^[a-zA-Z0-9]$/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') {
                      e.preventDefault();
                    }
                    if (e.key === 'Enter') {
                      e.preventDefault(); // Prevent form submission or unwanted behavior
                      partyNameInputRef.current?.focus(); // Move focus to senderVatav field
                    }
                  }}
                  onChange={(e: any) => {
                    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                    formik.setFieldValue('party_code', value);
                  }}
                  value={formik.values.party_code}
                  onFocus={handleFocus}
                />
              )}
              {formik.touched.party_code && formik.errors.party_code && (
                <div className="fv-plugins-message-container text-danger">
                  <span role="alert">{formik.errors.party_code as any}</span>
                </div>
              )}
            </Col>
            <Col md={4}>
              <label className="required fw-bold fs-6 mb-2">Party Name</label>
              <input
                type="text"
                name="party_name"
                ref={partyNameInputRef}
                className="form-control"
                autoComplete="off"
                maxLength={50}
                placeholder="Party Name"
                onKeyDown={(e: any) => {
                  if (!/^[a-zA-Z0-9 @%]$/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') {
                    e.preventDefault();
                  }
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent form submission or unwanted behavior
                    phoneInputRef.current?.focus(); // Move focus to senderVatav field
                  }
                }}
                onChange={(e: any) => {
                  const filteredValue = e.target.value.replace(/[^A-Za-z0-9\s,/@#&"*()%]/g, '').trimStart();
                  formik.setFieldValue('party_name', filteredValue);
                }}
                value={formik.values.party_name}
                onFocus={handleFocus}
              />
              {formik.touched.party_name && formik.errors.party_name && (
                <div className="fv-plugins-message-container text-danger">
                  <span role="alert">{formik.errors.party_name as any}</span>
                </div>
              )}
            </Col>
            <Col md={4}>
              <label className="fw-bold fs-6 mb-2">Phone</label>
              <input
                type="text"
                name="party_phone"
                className="form-control"
                ref={phoneInputRef}

                autoComplete="off"
                maxLength={11}
                placeholder="Phone"
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  const key = e.key;
                  const isNumeric = /^[0-9]$/.test(key);
                  const isSpecialKey =
                    key === 'Backspace' ||
                    key === 'Delete' ||
                    key === 'ArrowLeft' ||
                    key === 'ArrowRight' ||
                    key === 'ArrowUp' ||
                    key === 'ArrowDown' ||
                    (e.ctrlKey && (key === 'a' || key === 'c' || key === 'v'));
                  if (!isNumeric && !isSpecialKey && key !== 'Tab') {
                    e.preventDefault();
                  }
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent form submission or unwanted behavior
                    addressInputRef.current?.focus(); // Move focus to senderVatav field
                  }
                }}
                onChange={(e: any) => {
                  formik.setFieldValue('party_phone', e.target.value.trimStart());
                }}
                onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                  e.preventDefault();
                  const pastedData = e.clipboardData.getData('text');
                  const filteredValue = pastedData.replace(/[^0-9]/g, '');
                  formik.setFieldValue('party_phone', filteredValue);
                }}
                value={formik.values.party_phone}
                onFocus={handleFocus}
              />
              {formik.touched.party_phone && formik.errors.party_phone && (
                <div className="fv-plugins-message-container text-danger">
                  <span role="alert">{formik.errors.party_phone as any}</span>
                </div>
              )}
            </Col>
          </Row>
          <Row className="mb-8">
            <Col md={8}>
              <label className="fw-bold fs-6 mb-2">Address</label>
              <input
                type="text"
                name="party_address"
                className="form-control"
                ref={addressInputRef}

                autoComplete="off"
                maxLength={255}
                placeholder="Address"
                onKeyDown={(e: any) => {
                  if (!/^[a-zA-Z0-9 ]$/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') {
                    e.preventDefault();
                  }
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent form submission or unwanted behavior
                    citySelectRef.current?.focus(); // Move focus to senderVatav field
                  }
                }}
                onChange={(e: any) => {
                  formik.setFieldValue('party_address', e.target.value.trimStart());
                }}
                value={formik.values.party_address}
                onFocus={handleFocus}
              />
            </Col>
            <Col md={4}>
              <label className="required fw-bold fs-6 mb-2">City</label>
              <CreatableSelect
                ref={citySelectRef}
                options={cityOptions}
                className="mb-0 me-2"
                placeholder="Select or Create City"
                styles={customSelectStyles}
                onFocus={handleCityFocus}
                onMenuOpen={async () => {
                  if (cityOptions.length === 0) {
                    const cities = await citylisting();
                    setCityOptions(cities);
                  }
                }}
                menuPortalTarget={document.body}
                onChange={(selectedOption: SingleValue<{ label: string; value: string }>) => {
                  formik.setFieldValue('city_id', selectedOption ? selectedOption.value : '');
                  formik.setFieldTouched('city_id', false); // Prevent validation
                  // Move focus to the Vatav field
                  if (vatavInputRef.current) {
                    vatavInputRef.current.focus();
                  }
                }}
                value={cityOptions.find((option) => option.value === String(formik.values.city_id))}
                onCreateOption={async (inputValue: string) => {
                  if (!inputValue.trim()) {
                    console.error('City name cannot be empty');
                    return;
                  }
                  const newCity = await addCity(inputValue);
                  if (newCity) {
                    const cities = await citylisting();
                    setCityOptions(cities);
                    const createdCity = cities.find((city: { label: any }) => city.label === inputValue);
                    if (createdCity) {
                      formik.setFieldValue('city_id', createdCity.value);
                      formik.setFieldTouched('city_id', false); // Prevent validation
                      // Move focus to the Vatav field
                      if (vatavInputRef.current) {
                        vatavInputRef.current.focus();
                      }
                    }
                  }
                }}
                onKeyDown={handleCityKeyDown} // Add keydown handler
                onInputChange={(inputValue) => {
                  // Optionally handle input changes
                }}
              />
              {formik.touched.city_id && formik.errors.city_id && (
                <div className="fv-plugins-message-container text-danger">
                  <span role="alert">{formik.errors.city_id as any}</span>
                </div>
              )}
            </Col>
          </Row>
          <Row className="mb-8">
            <Col md={4}>
              <label className="fw-bold fs-6 mb-2">Vatav</label>
              <input
                type="text"
                name="party_vatav"
                className="form-control"
                autoComplete="off"
                maxLength={9}
                placeholder="Vatav"
                ref={vatavInputRef} // Add ref to the Vatav input
                onChange={(e: any) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    formik.setFieldValue('party_vatav', e.target.value.trimStart());
                  }
                }}
                onKeyDown={(e: any) => {
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent form submission or unwanted behavior
                    receiverLimitRef.current?.focus(); // Move focus to senderVatav field
                  }
                }}
                value={formik.values.party_vatav}
                onFocus={handleFocus}
              />
            </Col>
            <Col md={4}>
              <label className="required fw-bold fs-6 mb-2">Receiver Limit</label>
              <input
                type="text"
                name="party_lena_limit"
                className="form-control"
                ref={receiverLimitRef}
                autoComplete="off"
                maxLength={9}
                placeholder="Receiver Limit"
                onChange={(e: any) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    formik.setFieldValue('party_lena_limit', e.target.value.trimStart());
                  }
                }}
                value={formik.values.party_lena_limit}
                onFocus={handleFocus}
                onKeyDown={(e: any) => {
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent form submission or unwanted behavior
                    senderLimitRef.current?.focus(); // Move focus to senderVatav field
                  }
                }}
              />
              {formik.touched.party_lena_limit && formik.errors.party_lena_limit && (
                <div className="fv-plugins-message-container text-danger">
                  <span role="alert">{formik.errors.party_lena_limit as any}</span>
                </div>
              )}
            </Col>
            <Col md={4}>
              <label className="required fw-bold fs-6 mb-2">Sender Limit</label>
              <input
                type="text"
                name="party_dena_limit"
                className="form-control"
                ref={senderLimitRef}

                autoComplete="off"
                maxLength={9}
                placeholder="Sender Limit"
                onKeyDown={(e: any) => {
                  if (!/^[0-9.]$/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') {
                    e.preventDefault();
                  }
                  
                    if (e.key === 'Enter') {
                      e.preventDefault(); // Prevent form submission or unwanted behavior
                      openingBalanceRef.current?.focus(); // Move focus to senderVatav field
                    }
                  
                }}
                onChange={(e: any) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    formik.setFieldValue('party_dena_limit', e.target.value.trimStart());
                  }
                }}
                value={formik.values.party_dena_limit}
                onFocus={handleFocus}
              />
              {formik.touched.party_dena_limit && formik.errors.party_dena_limit && (
                <div className="fv-plugins-message-container text-danger">
                  <span role="alert">{formik.errors.party_dena_limit as any}</span>
                </div>
              )}
            </Col>
          </Row>
          <Row className="mb-8">
            <Col md={4}>
              <label className="required fw-bold fs-6 mb-2">Opening Balance</label>
              <input
                className={clsx('form-control')}
                type="text"
                name="party_opening_balance"
                maxLength={9}
                ref={openingBalanceRef}
                autoComplete="off"
                style={{ backgroundColor: itemIdForUpdate ? 'hsl(0, 0%, 95%)' : 'white' }}
                disabled={itemIdForUpdate ? true : false}
                placeholder="Opening Balance"
                onKeyDown={(e: any) => {
                  if (!/^[0-9.]$/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') {
                    e.preventDefault();
                  }
                  // if (e.key === 'Enter') {
                  //   e.preventDefault(); // Prevent form submission or unwanted behavior
                  //   crdrRef.current?.focus(); // Move focus to senderVatav field
                  // }
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if(id){
                      btnRef.current.focus();
                    }
                    if (crdrRef.current && !id) { // Only focus if not disabled
                      crdrRef.current.focus();
                      crdrRef.current.openMenu();
                    }
                  }
                }}
                onChange={(e: any) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    formik.setFieldValue('party_opening_balance', value);
                  }
                }}
                value={formik.values.party_opening_balance}
                onFocus={handleFocus}
              />
              {formik.touched.party_opening_balance && formik.errors.party_opening_balance && (
                <div className="fv-plugins-message-container text-danger">
                  <span role="alert">{formik.errors.party_opening_balance as any}</span>
                </div>
              )}
            </Col>
            <Col md={4}>
              <label className="fw-bold fs-6 mb-2 opacity-0">Credit/Debit</label>
              <Select
                className="mb-3"
                ref={crdrRef}
                options={optionOfCrDr}
                isDisabled={id ? true : false}
                styles={customSelectStyles}
                onChange={(e: SingleValue<{ value: string }>) => {
                  formik.setFieldValue('opening_bal_crordr', e ? e.value : '');
                }}
                value={optionOfCrDr.find((option: any) => option.value === formik.values.opening_bal_crordr)}
              />
            </Col>
          </Row>
        </div>
        <div className="text-end pe-4 mb-10 pt-5">
          <button
            type="submit"
            ref={btnRef}
            className="btn btn-primary px-20 me-3"
            data-kt-Parties-modal-action="submit"
            disabled={formik.isSubmitting}
          >
            <span className="indicator-label">
              {loading
                ? `${intl.formatMessage({ id: 'PLEASE.WAIT' })}`
                : `${intl.formatMessage({ id: 'BUTTONS.SUBMIT' })}`}
            </span>
          </button>
          <button
            type="reset"
            onClick={() => {
              cancel();
            }}
            className="btn btn-light px-20"
            data-kt-Parties-modal-action="cancel"
          >
            Cancel
          </button>
        </div>
      </form>
      {(formik.isSubmitting || isPartyLoading) && <PartysListLoading />}
    </>
  );
};

export { PartyEditModalForm };